import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import {
  ICONS,
  Modal,
  Button,
  Typography,
  TYPOGRAPHY_SIZE,
  ExpandableTable,
  PLATFORM_THEME_COLORS,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import PermissionCheck from 'src/components/permission-check';
import { useGetPermissionsGroupsForTable } from 'src/hooks/permission-group/get-permissions-groups.hook';
import { useDeletePermissionGroup } from 'src/hooks/permission-group/delete-permission-group.hook';

import {
  IPermissionGroup,
  IPermissionGroupToDelete,
  ICloseChangeModalAttempt,
} from './permission-group.interfaces';
import {
  PERMISSIONS_GROUP_MODALS,
  DEFAULT_PERMISSION_GROUP_TO_DELETE,
} from './permission-group.constants';
import { ModalForm } from './components/modal-form';
import { ViewDetails } from './components/view-details';
import { MainContainer, HeaderContainer } from './permission-group.styles';
import { ChangePermissionGroup } from './components/change-permission-group';

const PermissionGroup = () => {
  const { t } = useTranslation(['settings', 'common']);
  const [activeModal, setActiveModal] = useState<string | null>();
  const [isCloseGroupModalAttempt, setCloseGroupModalAttempt] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<IPermissionGroupToDelete>(
    DEFAULT_PERMISSION_GROUP_TO_DELETE
  );

  const closeModal = () => {
    setActiveModal(null);
    setCloseGroupModalAttempt(false);
    setGroupToDelete(DEFAULT_PERMISSION_GROUP_TO_DELETE);
  };

  const currentCompanyId = useGetCurrentCompanyId();
  const { permissionsGroups, paginationConfig, onTableChange } =
    useGetPermissionsGroupsForTable(currentCompanyId);
  const deletePermissionGroup = useDeletePermissionGroup({ onSuccess: closeModal });

  const closeChangeModalAttempt = ({ forceClosing }: ICloseChangeModalAttempt = {}) => {
    if (isCloseGroupModalAttempt || forceClosing) closeModal();

    setCloseGroupModalAttempt(!isCloseGroupModalAttempt);
  };

  const deleteModalSubmit = () => {
    if (groupToDelete.permissionGroupId) {
      deletePermissionGroup(groupToDelete.permissionGroupId);
    }

    if (permissionsGroups.length <= 1) {
      paginationConfig.onChange(paginationConfig.current - 1);
    }
  };

  return (
    <MainContainer>
      <Modal
        isOpened={PERMISSIONS_GROUP_MODALS.createGroup === activeModal}
        onClose={closeModal}
        title={t('settings:permissionGroupSettings.labels.createNewPermissionGroup')}
      >
        <ModalForm closeModal={closeModal} />
      </Modal>
      <DeleteModal
        translate={t}
        isOpened={PERMISSIONS_GROUP_MODALS.deleteGroup === activeModal}
        onClose={closeModal}
        title={t('settings:permissionGroupSettings.labels.deleteGroup')}
        subTitle={t('settings:permissionGroupSettings.labels.confirmDelete', {
          permission: groupToDelete.name,
        })}
        onSubmit={deleteModalSubmit}
      />
      <Modal
        isOpened={PERMISSIONS_GROUP_MODALS.changeGroup === activeModal}
        onClose={closeChangeModalAttempt}
        title={t('settings:permissionGroupSettings.labels.changePermission')}
      >
        {groupToDelete.permissionGroupId && (
          <ChangePermissionGroup
            permissionGroupId={groupToDelete.permissionGroupId}
            isCloseGroupModalAttempt={isCloseGroupModalAttempt}
            closeChangeModalAttempt={closeChangeModalAttempt}
          />
        )}
      </Modal>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('common:labels.permissionGroups')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.permissionGroup}
          action={PERMISSION_ACTIONS.create}
        >
          <Button
            type="primary"
            onClick={() => setActiveModal(PERMISSIONS_GROUP_MODALS.createGroup)}
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.white}
          >
            {t('settings:permissionGroupSettings.labels.createNewGroup')}
          </Button>
        </PermissionCheck>
      </HeaderContainer>
      <ExpandableTable
        t={t}
        expandedRowRender={(record: IPermissionGroup) => (
          <ViewDetails
            record={record}
            removeGroup={() => {
              setGroupToDelete({ name: record.name, permissionGroupId: record._id });
              setActiveModal(PERMISSIONS_GROUP_MODALS.deleteGroup);
            }}
          />
        )}
        onChange={onTableChange}
        dataSource={permissionsGroups}
        paginationConfig={paginationConfig}
      >
        <ExpandableTable.Column
          sorter
          title={t('settings:permissionGroupSettings.labels.name')}
          key="name"
          dataIndex="name"
          render={(text: string) => <Typography>{text}</Typography>}
        />
      </ExpandableTable>
    </MainContainer>
  );
};

export default PermissionGroup;
